import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

/** Single state for user selection from the missing-entities pickers. */
export interface MissingEntitiesSelection {
  /** productId -> variantIds[] (for SPECIFIC_PRODUCT) */
  productSelection?: Record<string, string[]>;
  /** For product collection mode */
  collectionSelection?: { id: string; name: string }[];
  /** Selected store product tags (for PRODUCT_TAGS) */
  productTagsSelection?: string[];
  /** Selected store product types (for PRODUCT_TYPE) */
  productTypesSelection?: string[];
  /** Selected store product vendors (for PRODUCT_VENDOR) */
  productVendorsSelection?: string[];
  /** Selected customer segments (for CUSTOMER_SEGMENTS) */
  segmentSelection?: Array<{ id: string; name?: string }>;
}

interface MissingEntitiesModalState {
  isOpen: boolean;
  /** Requested entity types from the AI chatbot */
  missingEntities: string[];
  /** Per-missing-key picker context from the bot (e.g. condition_ids -> { operator }) */
  missingContext?: Record<string, unknown>;
  /** User's selection from the modal */
  selection: MissingEntitiesSelection;
}

const initialState: MissingEntitiesModalState = {
  isOpen: false,
  missingEntities: [],
  missingContext: undefined,
  selection: {},
};

const missingEntitiesSlice = createSlice({
  name: "missingEntities",
  initialState,
  reducers: {
    openMissingEntitiesModal: (
      state,
      action: PayloadAction<{ missingEntities: string[]; missingContext?: Record<string, unknown> }>,
    ) => {
      state.isOpen = true;
      state.missingEntities = action.payload.missingEntities ?? [];
      state.missingContext = action.payload.missingContext;
      state.selection = {};
    },
    closeMissingEntitiesModal: (state) => {
      state.isOpen = false;
      state.missingEntities = [];
      state.missingContext = undefined;
    },
    setMissingEntitiesSelection: (state, action: PayloadAction<MissingEntitiesSelection>) => {
      state.selection = { ...state.selection, ...action.payload };
    },
  },
});

export const {
  openMissingEntitiesModal,
  closeMissingEntitiesModal,
  setMissingEntitiesSelection,
} = missingEntitiesSlice.actions;

export const selectMissingEntitiesModal = (state: RootState) => state.missingEntities;

export default missingEntitiesSlice.reducer;
